import { jsx, jsxs } from "react/jsx-runtime";
import { compose } from "recompose";
import { wrapDialog, DropdownButton } from "@teselagen/ui";
import React from "react";
import { Classes, Icon, Menu, MenuItem, Tag } from "@blueprintjs/core";
import withEditorProps from "../withEditorProps";
import specialCutsiteFilterOptions from "../constants/specialCutsiteFilterOptions";
import {
  differenceBy,
  find,
  flatMap,
  forEach,
  intersectionBy,
  map,
  noop,
  sortBy
} from "lodash-es";
import SingleEnzymeCutsiteInfo from "../helperComponents/PropertiesDialog/SingleEnzymeCutsiteInfo";
import { showDialog } from "../GlobalDialogUtils";
import {
  aliasedEnzymesByName,
  cutSequenceByRestrictionEnzyme,
  defaultEnzymesByName
} from "@teselagen/sequence-utils";
import { withRestrictionEnzymes } from "./withRestrictionEnzymes";
import { getEnzymeAliases } from "../utils/editorUtils";
function getUserEnzymeGroups(p) {
  return p.enzymeGroupsOverride || window.getExistingEnzymeGroups();
}
function isGroup({ cutsiteOrGroupKey }) {
  const isUserCreatedGroup = cutsiteOrGroupKey.startsWith("__userCreatedGroup");
  const specialCutsiteFilterOption = specialCutsiteFilterOptions[cutsiteOrGroupKey];
  return isUserCreatedGroup || specialCutsiteFilterOption;
}
export const AdditionalCutsiteInfoDialog = compose(
  withEditorProps,
  withRestrictionEnzymes,
  wrapDialog({
    isDraggable: true,
    getDialogProps: (props) => {
      if (isGroup(props)) {
        return {
          title: /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
            "Group - \xA0",
            getGroupElAndCutsites(props).title
          ] })
        };
      } else {
        let c = props.allCutsites.cutsitesByName;
        const name = props.cutsiteOrGroupKey.toLowerCase();
        if (!c[name]) {
          const e = aliasedEnzymesByName[name];
          if (e) {
            const res = cutSequenceByRestrictionEnzyme(
              props.sequenceData.sequence,
              true,
              e
            );
            c = { ...c, [name]: res };
          }
        }
        return {
          title: /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
            "Enzyme - \xA0",
            /* @__PURE__ */ jsx(
              CutsiteTag,
              {
                noClick: true,
                showActiveText: true,
                allRestrictionEnzymes: props.allRestrictionEnzymes,
                cutsitesByNameActive: props.filteredCutsites.cutsitesByName,
                cutsitesByName: c,
                name: props.cutsiteOrGroupKey
              }
            )
          ] })
        };
      }
    }
  })
)(function(props) {
  const {
    dispatch,
    editorName,
    cutsiteOrGroupKey,
    allCutsites: { cutsitesByName },
    filteredCutsites: { cutsitesByName: cutsitesByNameActive },
    selectedAnnotationId,
    allRestrictionEnzymes
  } = props;
  let inner;
  const userEnzymeGroups = getUserEnzymeGroups(props);
  if (isGroup(props)) {
    const ret = getGroupElAndCutsites(props);
    const enzymesThatCutInSeq = ret.enzymesThatCutInSeq;
    const enzymesThatDontCutInSeq = ret.enzymesThatDontCutInSeq;
    inner = /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(
          DropdownButton,
          {
            minimal: true,
            menu: /* @__PURE__ */ jsx(Menu, { children: flatMap(
              [
                ...map(specialCutsiteFilterOptions),
                ...map(userEnzymeGroups, (v, key) => ({
                  label: key,
                  value: "__userCreatedGroup" + key
                }))
              ],
              ({ label, value }, key, i) => {
                if (value === cutsiteOrGroupKey) return [];
                return /* @__PURE__ */ jsx(
                  MenuItem,
                  {
                    onClick: () => {
                      showDialog({
                        dialogType: "CompareEnzymeGroupsDialog",
                        props: {
                          dialogProps: {
                            title: "Comparing Groups",
                            width: 450
                          },
                          group1: cutsiteOrGroupKey,
                          group2: value
                        }
                      });
                    },
                    text: `vs ${label}`
                  },
                  i
                );
              }
            ) }),
            text: "Compare.."
          }
        ),
        /* @__PURE__ */ jsx("br", {}),
        /* @__PURE__ */ jsx("br", {})
      ] }),
      /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between" }, children: [
        !!enzymesThatCutInSeq.length && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              overflow: "auto"
              // maxHeight: 400
            },
            children: enzymesThatCutInSeq.map((e, i) => /* @__PURE__ */ jsx(
              CutsiteTag,
              {
                allRestrictionEnzymes: props.allRestrictionEnzymes,
                cutsitesByName: props.allCutsites.cutsitesByName,
                cutsitesByNameActive,
                ...e
              },
              i
            ))
          }
        ) }),
        enzymesThatDontCutInSeq && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              overflow: "auto"
            },
            children: enzymesThatDontCutInSeq.map((e, i) => /* @__PURE__ */ jsx(
              CutsiteTag,
              {
                allRestrictionEnzymes: props.allRestrictionEnzymes,
                cutsitesByName: props.allCutsites.cutsitesByName,
                cutsitesByNameActive,
                ...e
              },
              i
            ))
          }
        ) })
      ] })
    ] });
  } else if (
    //single enzyme case
    cutsitesByName[cutsiteOrGroupKey.toLowerCase()] || allRestrictionEnzymes[cutsiteOrGroupKey.toLowerCase()] || aliasedEnzymesByName[cutsiteOrGroupKey.toLowerCase()]
  ) {
    const inTheseUserGroups = [];
    const cutsiteGroup = cutsitesByName[cutsiteOrGroupKey.toLowerCase()] || [];
    const enzyme = cutsiteGroup[0] ? cutsiteGroup[0].restrictionEnzyme : allRestrictionEnzymes[cutsiteOrGroupKey.toLowerCase()] || aliasedEnzymesByName[cutsiteOrGroupKey.toLowerCase()];
    if (cutsiteGroup.length && cutsiteGroup.length <= 3) {
      const { el } = getGroupElAndCutsites({
        ...props,
        cutsiteOrGroupKey: find(
          specialCutsiteFilterOptions,
          ({ cutsThisManyTimes }) => cutsThisManyTimes === cutsiteGroup.length
        ).value
      });
      inTheseUserGroups.push(el);
    }
    const enzymeName = enzyme.name;
    forEach(userEnzymeGroups, (nameArray, name) => {
      let isInGroup;
      forEach(nameArray, (n) => {
        if (n.toLowerCase() === enzymeName.toLowerCase()) {
          isInGroup = true;
        }
      });
      if (isInGroup) {
        const cutsiteOrGroupKey2 = "__userCreatedGroup" + name;
        const { el } = getGroupElAndCutsites({
          ...props,
          cutsiteOrGroupKey: cutsiteOrGroupKey2
        });
        inTheseUserGroups.push(el);
      }
    });
    inner = /* @__PURE__ */ jsxs("div", { children: [
      !!inTheseUserGroups.length && /* @__PURE__ */ jsx("div", { style: { marginBottom: 20 }, children: inTheseUserGroups }),
      /* @__PURE__ */ jsx(
        SingleEnzymeCutsiteInfo,
        {
          ...{
            dispatch,
            editorName,
            selectedAnnotationId,
            cutsiteGroup,
            enzyme,
            allRestrictionEnzymes,
            allCutsites: props.allCutsites,
            filteredCutsites: props.filteredCutsites
          }
        }
      )
    ] });
  } else {
    console.error(`we shouldn't be here!:`, cutsiteOrGroupKey);
    return null;
  }
  return /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: inner });
});
export const CompareEnzymeGroupsDialog = compose(
  withEditorProps,
  withRestrictionEnzymes,
  wrapDialog({
    isDraggable: true
  })
)(function(props) {
  const {
    group1,
    group2,
    filteredCutsites: { cutsitesByName: cutsitesByNameActive }
  } = props;
  const g1 = getGroupElAndCutsites({
    ...props,
    cutsiteOrGroupKey: group1
  });
  const g2 = getGroupElAndCutsites({
    ...props,
    cutsiteOrGroupKey: group2
  });
  const byNameLower = (n) => n.name.toLowerCase();
  const shared = intersectionBy(
    g1.allEnzymesInGroup,
    g2.allEnzymesInGroup,
    byNameLower
  );
  const g1Only = differenceBy(g1.allEnzymesInGroup, shared, byNameLower);
  const g2Only = differenceBy(g2.allEnzymesInGroup, shared, byNameLower);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: { display: "flex", justifyContent: "space-between" },
      className: Classes.DIALOG_BODY,
      children: [
        /* @__PURE__ */ jsx(
          Column,
          {
            dataTest: "tg-column-1",
            title: "In",
            header: g1.el,
            body: g1Only.map((e, i) => {
              return /* @__PURE__ */ jsx(
                CutsiteTag,
                {
                  allRestrictionEnzymes: props.allRestrictionEnzymes,
                  cutsitesByName: props.allCutsites.cutsitesByName,
                  cutsitesByNameActive,
                  ...e
                },
                i
              );
            })
          }
        ),
        /* @__PURE__ */ jsx(
          Column,
          {
            dataTest: "tg-column-2",
            title: "In Both",
            header: /* @__PURE__ */ jsxs("div", { children: [
              g1.el,
              " ",
              g2.el
            ] }),
            body: shared.map((e, i) => {
              return /* @__PURE__ */ jsx(
                CutsiteTag,
                {
                  allRestrictionEnzymes: props.allRestrictionEnzymes,
                  cutsitesByName: props.allCutsites.cutsitesByName,
                  cutsitesByNameActive,
                  ...e
                },
                i
              );
            })
          }
        ),
        /* @__PURE__ */ jsx(
          Column,
          {
            dataTest: "tg-column-3",
            title: "In",
            header: g2.el,
            body: g2Only.map((e, i) => {
              return /* @__PURE__ */ jsx(
                CutsiteTag,
                {
                  allRestrictionEnzymes: props.allRestrictionEnzymes,
                  cutsitesByName: props.allCutsites.cutsitesByName,
                  cutsitesByNameActive,
                  ...e
                },
                i
              );
            })
          }
        )
      ]
    }
  );
});
function isUserEnzymeGroup(group) {
  const isUserCreatedGroup = group.startsWith("__userCreatedGroup");
  return isUserCreatedGroup;
}
const getGroupElAndCutsites = ({
  enzymeGroupsOverride,
  cutsiteOrGroupKey,
  filteredRestrictionEnzymes,
  allCutsites
}) => {
  const userEnzymeGroups = enzymeGroupsOverride || window.getExistingEnzymeGroups();
  let isGroupActive;
  let label;
  const enzymesThatDontCutInSeq = [];
  let enzymesThatCutInSeq = [];
  forEach(filteredRestrictionEnzymes, (g) => {
    if (g.value === cutsiteOrGroupKey) {
      isGroupActive = true;
    }
  });
  if (cutsiteOrGroupKey === "type2s") {
    const nameArray = flatMap(
      defaultEnzymesByName,
      (e) => e.isType2S ? e.name : []
    );
    label = "Type IIS Enzymes";
    sortBy(nameArray).forEach((name) => {
      const cutsites = allCutsites.cutsitesByName[name.toLowerCase()];
      if (!cutsites) {
        enzymesThatDontCutInSeq.push({ name, sites: [] });
      } else {
        enzymesThatCutInSeq.push({ name, sites: cutsites });
      }
    });
  } else if (isUserEnzymeGroup(cutsiteOrGroupKey)) {
    const name = cutsiteOrGroupKey.replace("__userCreatedGroup", "");
    const nameArray = userEnzymeGroups[name];
    label = getUserGroupLabel({ name, nameArray });
    sortBy(nameArray).forEach((name2) => {
      const cutsites = allCutsites.cutsitesByName[name2.toLowerCase()];
      if (!cutsites) {
        enzymesThatDontCutInSeq.push({ name: name2, sites: [] });
      } else {
        enzymesThatCutInSeq.push({ name: name2, sites: cutsites });
      }
    });
  } else {
    const specialCutsiteFilterOption = specialCutsiteFilterOptions[cutsiteOrGroupKey];
    label = specialCutsiteFilterOption.label;
    enzymesThatCutInSeq = map(
      allCutsites.cutsitesByName,
      (cutsites) => cutsites.length === specialCutsiteFilterOption.cutsThisManyTimes ? { name: cutsites[0].name, sites: cutsites } : null
    ).filter((n) => n !== null);
  }
  const title = /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
    label,
    " \xA0",
    isGroupActive ? "" : "(inactive)"
  ] });
  const el = addCutsiteGroupClickHandler({
    cutsiteOrGroupKey,
    el: /* @__PURE__ */ jsx(Tag, { minimal: true, intent: isGroupActive && "primary", style: { margin: 3 }, children: label }),
    title
  });
  return {
    el,
    title,
    enzymesThatDontCutInSeq,
    enzymesThatCutInSeq,
    allEnzymesInGroup: [...enzymesThatCutInSeq, ...enzymesThatDontCutInSeq]
    // cutsitesInGroup: [] //tnrtodo
  };
};
export const CutsiteTag = ({
  allRestrictionEnzymes,
  showActiveText,
  cutsitesByNameActive,
  cutsitesByName,
  name,
  onWrapperClick,
  doNotShowCuts,
  noClick,
  forceOpenCutsiteInfo
}) => {
  const isHidden = getIsEnzymeHidden({ name, allRestrictionEnzymes });
  let numCuts = (cutsitesByName[name.toLowerCase()] || []).length;
  const aliases = getEnzymeAliases(name);
  aliases.forEach((alias) => {
    if (numCuts === 0) {
      numCuts = (cutsitesByName[alias.toLowerCase()] || []).length;
    }
  });
  const isActive = numCuts ? cutsitesByNameActive[name.toLowerCase()] ? "primary" : void 0 : void 0;
  const el = /* @__PURE__ */ jsx(
    Tag,
    {
      onClick: onWrapperClick,
      intent: isActive,
      style: { margin: 3, opacity: isHidden ? 0.5 : 1 },
      children: /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
        getCutsiteWithNumCuts({
          name,
          numCuts,
          doNotShowCuts
        }),
        showActiveText ? isHidden ? /* @__PURE__ */ jsx("span", { children: "\xA0 hidden" }) : isActive ? /* @__PURE__ */ jsx("span", { children: "\xA0 active" }) : /* @__PURE__ */ jsx("span", { children: "\xA0 inactive" }) : null
      ] })
    }
  );
  return addCutsiteGroupClickHandler({
    el,
    noClick,
    cutsiteOrGroupKey: name,
    forceOpenCutsiteInfo
  });
};
export const getUserGroupLabel = ({ name, nameArray }) => /* @__PURE__ */ jsxs(
  "span",
  {
    style: { display: "flex", alignItems: "center" },
    title: `User created enzyme group ${name} -- ${nameArray.join(" ")}`,
    children: [
      /* @__PURE__ */ jsx(Icon, { size: 10, icon: "user" }),
      "\xA0",
      name
    ]
  }
);
export const getCutsiteWithNumCuts = ({ name, numCuts, doNotShowCuts }) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      },
      children: [
        " ",
        /* @__PURE__ */ jsx("div", { children: name }),
        " ",
        !doNotShowCuts && /* @__PURE__ */ jsxs("div", { style: { fontSize: 12 }, children: [
          "\xA0(",
          numCuts,
          " cut",
          numCuts === 1 ? "" : "s",
          ")"
        ] })
      ]
    }
  );
};
export const addCutsiteGroupClickHandler = ({
  closeDropDown = noop,
  cutsiteOrGroupKey,
  el,
  noClick,
  forceOpenCutsiteInfo
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: noClick ? "tg-cutsite-label" : "tg-clickable-cutsite-label",
    style: noClick ? {} : { cursor: "pointer" },
    onClick: noClick ? void 0 : (e) => {
      const isInMultiSelect = e.target.closest(
        ".bp3-multi-select-popover"
      );
      if (!forceOpenCutsiteInfo && isInMultiSelect) return true;
      closeDropDown();
      showDialog({
        dialogType: "AdditionalCutsiteInfoDialog",
        props: {
          cutsiteOrGroupKey
        }
      });
    },
    children: el
  }
);
function Column({ dataTest, header, body, title }) {
  return /* @__PURE__ */ jsxs("div", { "data-test": dataTest, style: { flexGrow: "1", flexBasis: "0" }, children: [
    title,
    ":",
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          height: 60,
          marginBottom: 20,
          borderBottom: "1px solid lightgrey"
        },
        children: header
      }
    ),
    body
  ] });
}
function getIsEnzymeHidden({ name, allRestrictionEnzymes }) {
  let isHidden = true;
  if (allRestrictionEnzymes[name.toLowerCase()]) {
    isHidden = false;
  }
  return isHidden;
}
